"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils"; // 確保你有引入合併類別的 utils 函式

function MaintenanceMasteryConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="17889" className="w-full max-w-7xl mx-auto px-4 py-12 space-y-8">
        <div className="h-96 w-full bg-[#f5f1eb]/40 animate-pulse rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[...Array(4)].map((_, i) => <div key={i} className="h-80 bg-[#f5f1eb]/40 animate-pulse rounded-sm" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-20">
        <p className="text-[#000000] mb-4">載入百科文章失敗 / Failed to load articles.</p>
        <Button onClick={refetch} variant="outline" className="border-[#745a3f] text-[#745a3f] hover:bg-[#745a3f] hover:text-[#f5f1eb]">重新嘗試 / Try Again</Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-20">
        <p className="text-[#2b2420]/60">目前尚無相關文章 / No maintenance guides found.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const gridPosts = posts.slice(1);
  const getCategory = (post: any) => {
    return post.categories && post.categories.length > 0 ? post.categories[0].name : "知識分享 / INSIGHTS";
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };
  return <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20">

<div className="mb-4 md:mb-6" data-index={0}>
    <Card className="overflow-hidden border-none shadow-md bg-[#ffffff] hover:shadow-lg transition-shadow duration-300 rounded-sm">
      <div className="grid md:grid-cols-2 gap-0">
        <div className="relative h-64 md:h-auto min-h-[300px] w-full overflow-hidden">
          {featuredPost.featuredImage ? <img src={featuredPost.featuredImage} alt={featuredPost.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105" /> : <div className="absolute inset-0 bg-[#f5f1eb] flex items-center justify-center text-[#2b2420]/40">
              No Image
            </div>}
          <div className="absolute top-4 left-4">
            <Badge className="bg-[#745a3f] text-[#f5f1eb] hover:bg-[#745a3f]/90 text-xs tracking-wider px-3 py-1 rounded-sm shadow-sm border-none font-sans">
              {getCategory(featuredPost)}
            </Badge>
          </div>
        </div>
                 <div className="flex flex-col justify-center pt-3 pb-6 px-6 md:p-10 lg:p-12">
                     {/* 1. 日期與閱讀時間標籤：mb-4 改為 mb-2 */}
            <div className="flex items-center gap-4 text-xs tracking-wider text-[#2b2420]/60 mb-2 font-sans">
              <span className="flex items-center gap-1">
                <SafeHydrate><Calendar className="w-4 h-4" /></SafeHydrate>
                {formatDate(featuredPost.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                5 MIN READ
              </span>
            </div>
            
            {/* 2. 主標題：mb-4 改為 mb-3 */}
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-[#2b2420] mb-3 leading-tight">
              {featuredPost.title}
            </h3>
            
            {/* 3. 文章簡介：mb-6 改為 mb-4 */}
            <div className="text-[#2b2420]/70 mb-4 line-clamp-3 text-base md:text-lg font-sans leading-relaxed" dangerouslySetInnerHTML={{
              __html: featuredPost.excerpt || ""
            }} />
          <div className="pt-2">
            <Link to={featuredPost.link || "#"}>
              <Button variant="ghost" className="w-full justify-between hover:bg-[#f5f1eb]/50 group-hover:text-[#604f45] px-0 hover:px-4 transition-all text-xs tracking-widest font-sans">
                閱讀全文 / READ MORE
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Card>
  </div>


  {gridPosts.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-16 gap-y-12">
    {gridPosts.map((post, i) => <Card key={post.id} className="flex flex-col border-none shadow-sm hover:shadow-md transition-all duration-500 bg-[#ffffff] group overflow-hidden rounded-sm" data-index={i + 1}>
        {/* 1. 圖片區 */}
        <div className="relative h-60 w-full overflow-hidden">
          {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="absolute inset-0 bg-[#f5f1eb] flex items-center justify-center text-[#2b2420]/40">
              No Image
            </div>}
          <div className="absolute top-4 left-4">
            <Badge variant="secondary" className="bg-[#f5f1eb]/90 text-[#745a3f] backdrop-blur-sm hover:bg-[#f5f1eb] shadow-sm border-none text-xs tracking-wider font-sans">
              {getCategory(post)}
            </Badge>
          </div>
        </div>
        
        {/* 2. 標題與日期區 
            - pt-3：大幅拉近與上方圖片的垂直落差（由 24px 縮緊至 12px）
            - pb-0：消除底部的額外推擠留白
         */}
        <CardHeader className="pt-3 pb-0 px-6">
          <div className="flex items-center gap-3 text-[10px] tracking-widest text-[#2b2420]/50 mb-1.5 font-sans uppercase">
            <span className="flex items-center gap-1">
              <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate>
              {formatDate(post.date)}
            </span>
          </div>
          <CardTitle className="font-serif text-xl md:text-2xl leading-snug text-[#2b2420] group-hover:text-[#745a3f] transition-colors">
            {post.title}
          </CardTitle>
        </CardHeader>
        
        {/* 3. 文章簡介內容區
            - pt-0：移除元件預設填充，配合 mt-1.5 讓文字自然緊貼在標題正下方
            - pb-1：收窄底部，讓下方的按鈕往上吸附
         */}
        <CardContent className="px-6 pt-0 pb-1 mt-1.5">
          <div className="text-[#2b2420]/70 text-sm line-clamp-3 font-sans leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt || ""
          }} />
        </CardContent>
        
        {/* 4. 頁尾按鈕區
            - pt-0：徹底拔除按鈕上方的空白推擠
            - pb-5：維持底部卡片邊緣的精緻收尾留白
         */}
        <CardFooter className="pt-0 pb-5 px-6">
          <Link to={post.link || "#"} className="w-full">
            <Button variant="ghost" className="w-full justify-between hover:bg-[#f5f1eb]/50 group-hover:text-[#604f45] px-0 hover:px-4 transition-all text-xs tracking-widest font-sans">
              閱讀全文 / READ MORE
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </CardFooter>
      </Card>)}
  </div>}
</div>;
}
export default function MaintenanceMastery() {
  //  1. 宣告動態狀態與觀察節點 (Intersection Observer)
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      // 當百科區塊滑入視窗內 15% 時觸發進入動畫
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (sectionRef.current) observer.unobserve(sectionRef.current); // 單次觸發，防禦重複閃爍
      }
    }, {
      threshold: 0.15
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  return <section ref={sectionRef} id="百科專欄-knowledge-hub" className="bg-[#ffffff] text-[#2b2420] w-full">
      {/* 標題區 */}
      {/*  2. 整合動畫過渡樣式：根據 isVisible 狀態，花費 1.2 秒由下往上平滑浮現 */}
      <div className={cn("mb-6 md:mb-12 text-center transition-all duration-[1.2s] ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="relative w-fit mx-auto flex flex-col items-end">
           
          {/* 1. 標題主體 */}
          <h2 className="text-[1.15rem] sm:text-2xl md:text-4xl font-hk-regular tracking-tight relative z-10 whitespace-nowrap text-[#3d3d3d]">
            
            {/*  3. 完美的下半部螢幕劃線色塊（已被安全地鎖死在 h2 內部，絕不被下方小字拉跑位置） */}
            <div className="absolute left-0 bottom-1 w-full h-2.5 bg-[#D9C5B2]/80 -z-10 pointer-events-none" />
            
            超耐磨木地板 | 那些關於地板的小事
          </h2>

          {/* 2. DISCOVERY 小字：w-full 與 text-right，強迫英文字母 Y 精準貼齊「事」的右側邊界 */}
          <p className="text-[10px] md:text-sm font-sans font-medium tracking-[0.2em] uppercase text-[#d3bfae] mt-2 w-full text-right">
            /DISCOVERY
          </p>

        </div>
      </div>

      {/*  4. 下方動態消費者：多包覆一層動態控制，讓文章跟著標題的節奏，隨滾動一同優雅淡入 */}
      <div className={cn("transition-all duration-[1.5s] delay-150 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <PostsProvider wp_query={{
        per_page: 5,
        orderby: "date",
        order: "desc"
      }}>
          <MaintenanceMasteryConsumer />
        </PostsProvider>
      </div>
    </section>;
}